import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAppState } from '../context/AppContext';
import { fetchInspection, inspectionImageUrl } from '../services/api';
import { exportCoaPdf } from '../utils/exportCoaPdf';
import './CoaPage.css';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('th-TH');
  } catch {
    return '—';
  }
}

function docNo(row) {
  const pdr = String(row?.meta?.pdr || '').replace(/\s+/g, '') || 'SCAN';
  const d = (row?.createdAt || '').slice(0, 10).replace(/-/g, '') || '00000000';
  const short = String(row?.id || '').slice(0, 6).toUpperCase();
  return `COA-${pdr}-${d}-${short}`;
}

function buildTestRows(row, rules) {
  const spots = Array.isArray(row?.spots) ? row.spots : [];
  const defects = Array.isArray(row?.defects) ? row.defects : [];
  const maxAllowed = Number(rules?.maxBlackSpotDiameterMm ?? 5);
  const maxCount = Number(rules?.maxBlackSpotsPerSqFt ?? 5);
  // Align with Table 3: soft small "wood_knot" labels count as pulp spots
  const pulpSpots = spots.filter((s) => {
    const d = Number(s.diameterMm) || 0;
    if (s.type === 'wood_knot' && (s.soft === true || d < 4) && d <= maxAllowed) return true;
    return s.type !== 'wood_knot';
  });
  const knotSpots = spots.filter((s) => {
    const d = Number(s.diameterMm) || 0;
    if (s.type === 'wood_knot' && (s.soft === true || d < 4) && d <= maxAllowed) return false;
    return s.type === 'wood_knot';
  });
  const maxDia = pulpSpots.reduce((m, s) => Math.max(m, Number(s.diameterMm) || 0), 0);
  const count = pulpSpots.length || (row?.spotCount ?? spots.length);
  const knotCount =
    knotSpots.length ||
    defects.filter((d) => d.type === 'wood_knot' || d.type === 'color_stain' || d.type === 'calender_spot')
      .length;
  const isPass = row?.verdict !== 'NG';

  const rows = [
    {
      item: 'จำนวนจุดด่างดำ / Black pulp spots',
      criteria: `≤ ${maxCount} จุด / ตร.ฟุต (LFB-QAD-SP-004)`,
      result:
        row?.spotsPerSqFt != null
          ? `${count} จุด (≈ ${row.spotsPerSqFt}/ตร.ฟุต)`
          : `${count} จุด`,
      ok:
        row?.spotsPerSqFt != null
          ? Number(row.spotsPerSqFt) <= maxCount
          : count <= maxCount,
    },
    {
      item: 'ขนาดจุดด่างดำ / Max spot size',
      criteria: `≤ ${maxAllowed} มม.`,
      result: maxDia > 0 ? `${maxDia.toFixed(2)} มม.` : '0 มม.',
      ok: maxDia <= maxAllowed || count === 0,
    },
    {
      item: 'ตาไม้ / Wood knot',
      criteria: 'ต้องไม่มี (0)',
      result: knotCount > 0 ? `พบ ${knotCount}` : 'ไม่พบ',
      ok: knotCount === 0,
    },
    {
      item: 'สรุปผลการตรวจ / Inspection verdict',
      criteria: 'ผ่านเกณฑ์ Appearance ทั้งหมด',
      result: isPass ? 'PASS' : 'REJECT',
      ok: isPass,
    },
  ];

  return rows;
}

export default function CoaPage() {
  const { id } = useParams();
  const { rules } = useAppState();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchInspection(id)
      .then((data) => {
        if (alive) setRow(data);
      })
      .catch((err) => {
        if (alive) setError(err.message || 'โหลดเอกสารไม่สำเร็จ');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const isPass = row?.verdict !== 'NG';
  const watermarkText = isPass
    ? 'Pass Lee Fiberboard Co., Ltd.'
    : 'Reject Lee Fiberboard Co., Ltd.';
  const imageUrl = inspectionImageUrl(row);
  const meta = row?.meta || {};
  const testRows = useMemo(() => (row ? buildTestRows(row, rules) : []), [row, rules]);
  const failures = Array.isArray(row?.failures) ? row.failures : [];

  async function handleExportPdf() {
    if (!pageRef.current || exporting) return;
    setExporting(true);
    try {
      await exportCoaPdf(pageRef.current, `${docNo(row)}.pdf`);
    } catch (err) {
      window.alert(err?.message || 'สร้าง PDF ไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="coa-screen">
        <p className="muted">กำลังสร้างเอกสาร COA...</p>
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="coa-screen stack">
        <p style={{ color: 'var(--ng)' }}>{error || 'ไม่พบผลสแกน'}</p>
        <Link className="btn btn-primary" to="/data">
          กลับไปข้อมูล
        </Link>
      </div>
    );
  }

  return (
    <div className="coa-screen">
      <div className="coa-toolbar no-print">
        <Link className="btn" to={`/scan/result/${id}`}>
          ← กลับ
        </Link>
        <div className="coa-toolbar-actions">
          <button
            className="btn btn-primary"
            type="button"
            disabled={exporting}
            onClick={handleExportPdf}
          >
            {exporting ? 'กำลังสร้าง PDF...' : 'บันทึก PDF'}
          </button>
          <button className="btn" type="button" onClick={() => window.print()}>
            พิมพ์
          </button>
        </div>
      </div>

      <div className="coa-page-frame">
      <article
        ref={pageRef}
        className={`coa-page ${isPass ? 'is-pass' : 'is-reject'}`}
      >
        <div className="coa-watermark" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i}>{watermarkText}</span>
          ))}
        </div>

        <header className="coa-header">
          <div className="coa-brand">
            <img
              className="coa-logo-img"
              src="/lee-fibreboard-logo.png"
              alt="Lee Fibreboard Co., Ltd."
            />
            <div>
              <strong>Lee Fibreboard Co., Ltd.</strong>
              <p>บริษัท ลี้ไฟเบอร์บอร์ด จำกัด</p>
            </div>
          </div>
          <div className="coa-docmeta">
            <h1>COA</h1>
            <p>Certificate of Analysis</p>
            <p>ใบรับรองผลการวิเคราะห์</p>
            <dl>
              <div>
                <dt>เลขที่ / No.</dt>
                <dd>{docNo(row)}</dd>
              </div>
              <div>
                <dt>วันที่ / Date</dt>
                <dd>{formatDate(row.createdAt)}</dd>
              </div>
            </dl>
          </div>
        </header>

        <section className={`coa-banner ${isPass ? 'ok' : 'ng'}`}>
          <div className="coa-banner-mark" aria-hidden="true">
            {isPass ? '✓' : '✕'}
          </div>
          <div>
            <strong>
              {isPass ? 'ผ่านการตรวจ — PASS' : 'ไม่ผ่านการตรวจ — REJECT'}
            </strong>
            <p>
              {isPass
                ? 'ผลการตรวจเป็นไปตามเกณฑ์ที่กำหนด / All quality criteria passed'
                : 'พบข้อบกพร่องเกินเกณฑ์ที่กำหนด / Defects exceeded specification limits'}
            </p>
          </div>
        </section>

        <section className="coa-info-grid">
          <div className="coa-info-fields">
            <div className="coa-field">
              <span>ชื่อบริษัท / Customer</span>
              <strong>{meta.customerName || '—'}</strong>
            </div>
            <div className="coa-field">
              <span>PDR งาน / Job PDR</span>
              <strong>{meta.pdr || '—'}</strong>
            </div>
            <div className="coa-field">
              <span>เกรดกระดาษ / Paper Grade</span>
              <strong>
                {[meta.grade, meta.gram ? `${meta.gram}g` : '', meta.paperWidth ? `W${meta.paperWidth}` : '']
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </strong>
            </div>
            <div className="coa-field">
              <span>ขนาดงาน / Job Size (mm)</span>
              <strong>
                {meta.sizeW && meta.sizeH
                  ? `${meta.sizeW}×${meta.sizeH}`
                  : meta.size || '—'}
              </strong>
            </div>
            <div className="coa-field">
              <span>ชื่อผู้ตรวจ / Inspector</span>
              <strong>{meta.inspector || meta.operator || '—'}</strong>
            </div>
            <div className="coa-field">
              <span>วันเวลาตรวจ / Inspected at</span>
              <strong>{formatDateTime(row.createdAt)}</strong>
            </div>
          </div>

          <aside className="coa-photo">
            <p>ภาพถ่ายขณะตรวจสอบ / Captured Photo</p>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="หลักฐานการตรวจ"
                onLoad={(e) => {
                  const img = e.currentTarget;
                  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    img.style.aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
                  }
                }}
              />
            ) : (
              <div className="coa-photo-empty">ไม่มีภาพ</div>
            )}
          </aside>
        </section>

        <section className="coa-table-wrap">
          <h2>ผลการทดสอบ / Test Results</h2>
          <table className="coa-table">
            <thead>
              <tr>
                <th>#</th>
                <th>รายการทดสอบ / Test Item</th>
                <th>เกณฑ์ / Criteria</th>
                <th>ผลที่ได้ / Result</th>
                <th>สรุป / Verdict</th>
              </tr>
            </thead>
            <tbody>
              {testRows.map((r, i) => (
                <tr key={r.item}>
                  <td>{i + 1}</td>
                  <td>{r.item}</td>
                  <td>{r.criteria}</td>
                  <td>{r.result}</td>
                  <td>
                    <span className={`coa-tag ${r.ok ? 'ok' : 'ng'}`}>
                      {r.ok ? 'PASS' : 'FAIL'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {!isPass && failures.length > 0 ? (
          <section className="coa-defects">
            <h2>ข้อบกพร่องที่พบ / Defects Found</h2>
            <ul>
              {failures.map((f) => (
                <li key={f.code || f.message}>{f.message || f.code}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="coa-signs">
          <div>
            <div className="coa-sign-box">
              <img
                className="coa-sign-img"
                src="/sign-inspector.png"
                alt="ลายเซ็นผู้ตรวจสอบ"
              />
            </div>
            <strong>ผู้ตรวจสอบ / Inspector</strong>
            <p>วัชราภรณ์ พาวินิจ</p>
            <p>วันที่ {formatDate(row.createdAt)}</p>
          </div>
          <div>
            <div className="coa-sign-box coa-sign-box-empty" />
            <strong>ผู้จัดการ QA / QA Manager</strong>
            <p>........................</p>
            <p>วันที่ ............</p>
          </div>
          <div>
            <div className="coa-sign-box">
              <img
                className="coa-sign-img"
                src="/sign-approver.png"
                alt="ลายเซ็นผู้อนุมัติ"
              />
            </div>
            <strong>ผู้อนุมัติ / Authorized</strong>
            <p>กฤษณะ สมสงวน</p>
            <p>วันที่ {formatDate(row.createdAt)}</p>
          </div>
        </section>

        <footer className="coa-footer">
          <span>Issued by Rollscan — Lee Fiberboard Co., Ltd. · Do not alter</span>
          <span>{docNo(row)} · Page 1/1</span>
        </footer>
      </article>
      </div>
    </div>
  );
}
